import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { EMPTY, catchError, throwError } from 'rxjs';
import { AuthService } from '@core/services/auth.service';
import { CompraTokenStore } from '@core/services/compra-token.store';

const SKIP_AUTH = ['/auth/auth/login', '/auth/auth/forgot', '/auth/auth/validatereset', '/auth/auth/reset'];

// Las únicas rutas donde el backend acepta el token de compra (X-Compra-Token):
// las pantallas de compra de plan, a las que llega un usuario bloqueado por
// plan vencido, que no tiene sesión, y el retorno de PayPhone de plan y firma.
// En cualquier otra el backend lo ignora, pero tampoco se manda: no hay motivo
// para exponerlo.
const RUTAS_DE_COMPRA = [
  '/admin/planes/getmetaplanes', '/admin/planes/comprarplan',
  '/admin/planes/confirmarpago', '/admin/planes/cancelarpago',
  '/admin/firma/confirmarpago', '/admin/firma/cancelarpago',
];

// El retorno de PayPhone: el pago ya está hecho y confirmarlo no puede depender de
// la sesión. Si hay token de compra se manda ese y NO el Bearer: uno vencido daría
// 401 y el interceptor sacaría al usuario al login antes de registrar el cobro.
const RUTAS_DE_RETORNO_DE_PAGO = RUTAS_DE_COMPRA.filter(u => /(confirmar|cancelar)pago$/.test(u));

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const auth   = inject(AuthService);
  const router = inject(Router);
  const dialog = inject(MatDialog);

  const skip = SKIP_AUTH.some(u => req.url.includes(u));
  const token = auth.getToken();

  let outReq = (!skip && token)
    ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
    : req;

  // Con sesión iniciada manda el Bearer de siempre; el token de compra es para quien
  // no tiene sesión y, en el retorno de PayPhone, también para quien la tiene (puede
  // haber vencido mientras pagaba).
  const esRetornoDePago = RUTAS_DE_RETORNO_DE_PAGO.some(u => req.url.includes(u));
  if ((!token || esRetornoDePago) && RUTAS_DE_COMPRA.some(u => req.url.includes(u))) {
    const compra = inject(CompraTokenStore).get();
    if (compra) outReq = req.clone({ setHeaders: { 'X-Compra-Token': compra } });
  }

  return next(outReq).pipe(
    catchError((err: HttpErrorResponse) => {
      if (err.status === 401) {
        // Sesión expirada. Una pantalla puede tener varias peticiones en
        // vuelo a la vez (p.ej. Nueva Compra carga ~4 catálogos en
        // paralelo al entrar) — todas 401ean casi al mismo tiempo. Sin la
        // guarda de auth.isLoggedIn(), cada una disparaba su propio ciclo
        // completo (cerrar diálogos + logout + navigate): el navigate()
        // interno de logout() (sin returnUrl) competía en una carrera real
        // contra el de acá (con returnUrl) — a veces ganaba el que no lo
        // tenía, dejando al usuario en /login sin poder volver a la pantalla
        // de origen. Peor: logout() disparaba además una llamada HTTP de
        // logout con ese mismo token ya inválido, que volvía a 401 y
        // relanzaba TODO el ciclo de nuevo (incluida otra navegación) por
        // cada petición original. Ahora solo la primera 401 de la ráfaga
        // limpia la sesión y navega una única vez.
        if (auth.isLoggedIn()) {
          const returnUrl = router.url;
          // Sesión expirada con un diálogo (MatDialog/CDK overlay) abierto: al
          // vivir fuera del <router-outlet> (se appendea a <body>), navegar a
          // /login NO lo cierra — quedaba flotando encima de la pantalla de
          // login, y su propio subscribe({error}) todavía mostraba el mensaje
          // genérico de esa pantalla (p.ej. "Error al buscar clientes") aunque
          // ya no tuviera sentido. Cerrarlo acá, antes de redirigir.
          dialog.closeAll();
          // Limpia el estado local sin llamar al backend (el token ya es
          // inválido — llamarlo solo generaría otro 401 en cascada) ni
          // navegar por su cuenta (acá es donde se decide el returnUrl).
          auth.clearSession();
          router.navigate(['/login'], {
            queryParams: returnUrl && returnUrl !== '/login' ? { returnUrl } : undefined,
          });
        }
        // Nunca se re-lanza un 401 al subscribe() original: varios call
        // sites de este proyecto hacen `.subscribe({ next: ... })` sin
        // manejador de error, y un 401 acá terminaba como una excepción sin
        // capturar justo en medio de la redirección a /login.
        return EMPTY;
      }
      return throwError(() => err);
    })
  );
};
